import { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search, X, Check, Ban } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { HEALTH_ICON_MANIFEST } from '../../icons/health';
import HealthIcon from '../../icons/health/HealthIcon';
import { useSpecialtyIconStyle } from '../../specialtyIcons';
import { useAnchoredDropdown } from './selectStyles';

// Rail order for the group headers: the specialty drawings first because
// that is what the two masters almost always want; the body-part / condition
// / generic sets follow; `custom` (the UX person's own SVGs) last, and only
// when the generated manifest has any.
const GROUP_ORDER = ['specialties', 'anatomy', 'conditions', 'healthcare', 'custom'];

// Group label keys live in the gallery's namespace so the picker and the
// gallery page never disagree on a group's name.
const groupLabelKey = (group) => `specialityicons.group_${group}`;

// One option row. Hoisted so the list does not re-create the component per
// render. Every icon draws in the ONE platform style: selection is shown by
// the highlight and the Check, never by swapping the drawing — a picker that
// mixed outline and solid rows is exactly what the platform setting replaced.
const OptionRow = ({ entry, isSelected, iconStyle, onSelect, testId }) => (
    <button
        type="button"
        onClick={() => onSelect(entry.key)}
        className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-all duration-150 ${
            isSelected ? 'bg-[#4ABDBD]/10 text-[#4ABDBD] font-semibold' : 'text-gray-700 hover:bg-gray-50'
        }`}
        title={entry.key}
        data-testid={`${testId}-option-${entry.key}`}
    >
        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg shrink-0 ${isSelected ? 'bg-[#4ABDBD]/15 text-[#2D8A8A]' : 'bg-gray-100 text-gray-600'}`}>
            <HealthIcon iconKey={entry.key} variant={iconStyle} size={20} />
        </span>
        <span className="min-w-0 flex-1 text-left">
            <span className="block truncate">{entry.label}</span>
            <span className="block truncate font-mono text-[11px] text-gray-400 font-normal">{entry.key}</span>
        </span>
        {isSelected && <Check className="w-4 h-4 text-[#4ABDBD] flex-shrink-0" data-testid={`${testId}-option-check-icon-${entry.key}`} />}
    </button>
);

/**
 * Picks an icon KEY from the bundled health library (src/icons/health).
 * Modelled on CountrySelect: trigger with icon + label, portaled panel, a
 * search box over label + key, grouped rows. `value` is the key or null;
 * `onChange` receives the key, or null from the "No icon" row.
 */
const HealthIconSelect = ({
    value,
    onChange,
    placeholder,
    disabled = false,
    error = false,
    className = '',
    testId = 'health-icon-select',
}) => {
    const { t } = useTranslation();
    const iconStyle = useSpecialtyIconStyle();
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const containerRef = useRef(null);
    const searchInputRef = useRef(null);
    const panelOpen = isOpen && !disabled;
    const { anchorRef, panelRef, panelStyle } = useAnchoredDropdown(panelOpen);

    const selected = value ? HEALTH_ICON_MANIFEST.find((e) => e.key === value) : null;

    // Filtered rows, bucketed by group in rail order. Computed once per
    // keystroke, not once per row.
    const groups = useMemo(() => {
        const q = search.trim().toLowerCase();
        const matches = q
            ? HEALTH_ICON_MANIFEST.filter((e) => e.label.toLowerCase().includes(q) || e.key.includes(q))
            : HEALTH_ICON_MANIFEST;
        return GROUP_ORDER
            .map((group) => ({ group, entries: matches.filter((e) => e.group === group) }))
            .filter((g) => g.entries.length > 0);
    }, [search]);

    const close = () => {
        setIsOpen(false);
        setSearch('');
    };

    // Close on outside click. The panel is portaled to document.body, so it is
    // not a descendant of containerRef — without the panelRef test, mousedown
    // inside the panel would close it before the click lands.
    useEffect(() => {
        if (!isOpen) return undefined;
        const handleClickOutside = (e) => {
            if (containerRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
            close();
        };
        // Keyboard-closable: Escape from anywhere while open.
        const handleKey = (e) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                close();
                anchorRef.current?.focus();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKey);
        };
    }, [isOpen, panelRef, anchorRef]);

    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            setTimeout(() => searchInputRef.current?.focus(), 50);
        }
    }, [isOpen]);

    const handleSelect = (key) => {
        onChange(key);
        close();
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onChange(null);
        close();
    };

    // A stored key the bundle no longer knows (icon renamed in a release)
    // still shows as text so the admin sees what to replace.
    const unknownKey = value && !selected;

    return (
        <div ref={containerRef} className={`relative ${className}`} data-testid={`${testId}-container`}>
            <button
                ref={anchorRef}
                type="button"
                onClick={() => !disabled && setIsOpen(!isOpen)}
                disabled={disabled}
                className={`w-full px-4 py-2.5 rounded-lg border-2 text-sm font-medium transition-all duration-200 flex items-center justify-between gap-2 ${
                    error
                        ? 'border-red-300 bg-red-50'
                        : isOpen
                            ? 'border-[#4ABDBD] ring-2 ring-[#4ABDBD]/20 bg-white'
                            : 'border-gray-300 hover:border-[#4ABDBD] bg-white'
                } ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : 'cursor-pointer'}`}
                data-testid={`${testId}-trigger-button`}
            >
                <span className={`truncate flex items-center gap-2.5 ${!value ? 'text-gray-400' : 'text-gray-800'}`} data-testid={`${testId}-selected-text`}>
                    {selected && (
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-[#4ABDBD]/15 text-[#2D8A8A] shrink-0">
                            <HealthIcon iconKey={selected.key} variant={iconStyle} size={18} />
                        </span>
                    )}
                    {selected ? (
                        <span className="truncate">
                            {selected.label}
                            <span className="ml-2 font-mono text-[11px] text-gray-400 font-normal">{selected.key}</span>
                        </span>
                    ) : unknownKey ? (
                        <span className="truncate font-mono text-amber-600">{value}</span>
                    ) : (
                        placeholder || t('specialityicons.picker_placeholder', 'Choose an icon (optional)')
                    )}
                </span>
                <div className="flex items-center gap-1 flex-shrink-0">
                    {value && !disabled && (
                        <span
                            onClick={handleClear}
                            className="p-0.5 hover:bg-gray-100 rounded-full transition-colors"
                            data-testid={`${testId}-clear-button`}
                        >
                            <X className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600" />
                        </span>
                    )}
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                </div>
            </button>

            {/* Portaled at DROPDOWN_Z_INDEX so it floats over the modal that
                hosts the form — see selectStyles.js for the stacking story. */}
            {panelOpen && typeof document !== 'undefined' && createPortal(
                <div
                    ref={panelRef}
                    style={panelStyle}
                    className="flex flex-col bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden animate-in fade-in slide-in-from-top-1 duration-150"
                    data-testid={`${testId}-dropdown`}
                >
                    <div className="p-2 border-b border-gray-100 flex-shrink-0">
                        <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                ref={searchInputRef}
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder={t('specialityicons.picker_search', 'Search by name or key…')}
                                className="w-full pl-8 pr-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-[#4ABDBD] focus:ring-1 focus:ring-[#4ABDBD]/30 placeholder-gray-400 transition-all"
                                data-testid={`${testId}-search-input`}
                            />
                            {search && (
                                <button
                                    type="button"
                                    onClick={() => setSearch('')}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-200 rounded-full transition-colors"
                                    data-testid={`${testId}-search-clear-button`}
                                >
                                    <X className="w-3.5 h-3.5 text-gray-400" />
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="min-h-0 overflow-y-auto custom-scrollbar" data-testid={`${testId}-options-list`}>
                        {/* "No icon" — first, always, and never filtered out, so
                            clearing is one click even mid-search. */}
                        <button
                            type="button"
                            onClick={() => handleSelect(null)}
                            className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-all duration-150 border-b border-gray-100 ${
                                !value ? 'bg-[#4ABDBD]/10 text-[#4ABDBD] font-semibold' : 'text-gray-500 hover:bg-gray-50'
                            }`}
                            data-testid={`${testId}-option-none`}
                        >
                            <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100 text-gray-400 shrink-0">
                                <Ban size={18} />
                            </span>
                            <span className="flex-1 text-left">{t('specialityicons.picker_no_icon', 'No icon')}</span>
                            {!value && <Check className="w-4 h-4 text-[#4ABDBD] flex-shrink-0" />}
                        </button>

                        {groups.length === 0 ? (
                            <div className="px-4 py-6 text-center text-sm text-gray-400" data-testid={`${testId}-no-results-text`}>
                                {t('specialityicons.picker_no_results', 'No icons match')}
                            </div>
                        ) : (
                            groups.map(({ group, entries }) => (
                                <div key={group} data-testid={`${testId}-group-${group}`}>
                                    <div className="sticky top-0 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400 bg-gray-50/95 backdrop-blur-sm border-b border-gray-100">
                                        {t(groupLabelKey(group), group)}
                                    </div>
                                    {entries.map((entry) => (
                                        <OptionRow
                                            key={entry.key}
                                            entry={entry}
                                            isSelected={value === entry.key}
                                            iconStyle={iconStyle}
                                            onSelect={handleSelect}
                                            testId={testId}
                                        />
                                    ))}
                                </div>
                            ))
                        )}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default HealthIconSelect;
